import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WeeklyReportTab } from "@/components/kpi/WeeklyReportTab";
import { ProductMapping } from "@/components/kpi/ProductMapping";
import { ServiceMapping } from "@/components/kpi/ServiceMapping";
import { UnmatchedPacks } from "@/components/kpi/UnmatchedPacks";
import { AdSpendPanel } from "@/components/kpi/AdSpendPanel";
import { ClientLinksPanel } from "@/components/kpi/ClientLinksPanel";
import { CsvImportPanel } from "@/components/kpi/CsvImportPanel";
import { SourcesPanel } from "@/components/kpi/SourcesPanel";
import { NightlySyncAlert } from "@/components/kpi/NightlySyncAlert";
import { RulesPanel } from "@/components/kpi/RulesPanel";
import { AuditPanel, OffersPanel } from "@/components/kpi/OffersPanel";
import { errMsg } from "@/components/kpi/kpiFormat";

export default function KpiReports() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [services, setServices] = useState([]);
  const [dataKey, setDataKey] = useState(0);
  const tab = params.get("tab") || "report";

  const loadServices = () => api.get("/kpi/services").then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/kpi/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e)));
    loadServices();
  }, []);

  if (!meta) return <div className="p-8"><div className="h-64 animate-pulse rounded-lg bg-zinc-100" data-testid="kpi-page-loading" /></div>;
  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: true });

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="kpi-page">
      <header className="space-y-1">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Business reporting · formula {meta.formula_version}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">Geronimo weekly KPIs</h1>
        <p className="text-sm text-zinc-600 max-w-3xl">Saturday-to-Friday figures calculated from Acuity, Stripe, the enquiries inbox and your
          manual inputs. A draft is built every Saturday morning; the Director approves and locks it before export.</p>
      </header>
      <NightlySyncAlert status={meta.nightly_sync} onOpenSources={() => setTab("inputs")} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="report" data-testid="kpi-tab-report">Weekly report</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="kpi-tab-mapping">Product & service mapping</TabsTrigger>
          <TabsTrigger value="inputs" data-testid="kpi-tab-inputs">Ad spend & data</TabsTrigger>
          <TabsTrigger value="rules" data-testid="kpi-tab-rules">Rules & targets</TabsTrigger>
        </TabsList>
        <TabsContent value="report" className="mt-6">
          <WeeklyReportTab meta={meta} initialWeek={params.get("week")} />
        </TabsContent>
        <TabsContent value="mapping" className="mt-6 space-y-6">
          <ProductMapping canEdit={meta.can_configure} services={services} reloadServices={loadServices} />
          <UnmatchedPacks canEdit={meta.can_configure} />
          <ServiceMapping canEdit={meta.can_configure} services={services.filter((s) => s.kind !== "category")} />
        </TabsContent>
        <TabsContent value="inputs" className="mt-6 space-y-6">
          <AdSpendPanel key={`spend-${dataKey}`} />
          <ClientLinksPanel key={`links-${dataKey}`} />
          <SourcesPanel key={`src-${dataKey}`} canRunFriday={meta.can_approve} nextFriday={meta.next_friday_draft} />
          <CsvImportPanel columns={meta.csv_columns} onImported={() => setDataKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="rules" className="mt-6 space-y-6">
          <RulesPanel settings={meta.settings} services={services.filter((s) => s.kind !== "category")} canEdit={meta.can_configure} onSaved={(s) => setMeta((m) => ({ ...m, settings: s }))} />
          <OffersPanel canEdit={meta.can_configure} />
          <AuditPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
